<template>
    <div class="widget">
        <div class="widget-header">
            <div class="widget-header-item">
                <div class="widget-type">
                    {{$t('indexpage.gram_price')}}
                </div>
                <div class="widget-price">
                    <!-- <ui-fiat v-if="price" v-bind:tonValue="10 ** 9" />  -->
                    <span v-if="price">${{ price.toFixed(2) }}</span>
                    <span class="skeleton" v-else>NNNNN</span>
                </div>
            </div>
            <div class="widget-header-item">
                <div class="widget-type widget-type-market-cap">
                    {{$t('indexpage.market_overview')}}
                </div>
                <div class="widget-price widget-price-market-cap">
                    <span v-if="marketcap">${{ marketcap }}</span>
                    <span class="skeleton" v-else>NNNNN</span>
                </div>
            </div>
        </div>
        <ui-link v-bind:to="{ name: 'stats' }" class="widget-content widget-content--chart">
            <ui-stats-chart smooth hide-values
                v-bind:series="series"
                v-bind:height="150"
                v-bind:y-format="axisFormat"
                v-bind:loading="!points"/>
        </ui-link>
    </div>
</template>

<script>
import UiStatsChart from '~/components/stats/UiStatsChart.vue';
import { formatUsd, compactNumber } from '~/components/stats/statsFormat.js';
import { getMarketDataHistory } from '~/api/coingecko.js';
import { prefixNumber } from '~/lib/Chart.js/helpers.js';
import { getMarketQuotes } from '~/api/stats.js';

const WIDGET_POINTS = 12;

/**
 * Averages consecutive points into at most `count` evenly sized groups; the
 * last point stays as it is, so the curve ends on the current price.
 */
const averagePoints = function averagePoints(points, count) {
    if (points.length <= count) {
        return points;
    }
    const size = Math.ceil((points.length - 1) / (count - 1));
    const result = [];
    for (let i = 0; i < points.length - 1; i += size) {
        const slice = points.slice(i, Math.min(i + size, points.length - 1));
        result.push({
            t: slice.reduce((acc, p) => acc + p.t, 0) / slice.length,
            v: slice.reduce((acc, p) => acc + p.v, 0) / slice.length,
        });
    }
    result.push(points.at(-1));
    return result;
};

export default {
    data() {
        return {
            // A month of prices averaged to a dozen points, [{ t, v }], undefined while loading:
            points: undefined,
            marketcap: null,
            price: null,
        };
    },

    mounted() {
        // this.getMarketPrice();
        this.loadData();
    },

    computed: {
        series() {
            if (!this.points?.length) {
                return [];
            }
            return [{
                key: 'price',
                label: this.$t('indexpage.gram_price'),
                color: 'var(--blue-bright)',
                type: 'area',
                data: this.points,
                format: (value, locale) => formatUsd(value, locale),
            }];
        },
    },

    methods: {
        async getMarketPrice() {
            try {
                const getMarketPrice = await getMarketQuotes();
                this.price = getMarketPrice.quotes.usd.price;
                this.marketcap = prefixNumber(getMarketPrice.quotes.usd.market_cap, this.$i18n.locale);
            } catch (error) {
                console.error(error);
            }
        },

        async loadData() {
            try {
                // Up to 30 days the api answers in 15-minute points whatever the
                // interval asked for (3000 points, 254 KB), from 31 days on — in
                // daily ones (32 points, 3 KB). The daily series ends at midnight
                // UTC though, so the current price and market cap come from the
                // short request, the same one the store makes for the usd rate:
                const [history, recent] = await Promise.allSettled([
                    getMarketDataHistory({ currency: 'USD', days: 31, interval: 'daily' }),
                    getMarketDataHistory({ currency: 'USD', days: 2, interval: 'daily' }),
                ]);

                if (history.status === 'rejected' && recent.status === 'rejected') {
                    throw history.reason;
                }

                const latest = (recent.value || history.value);
                const current = [latest.prices.at(-1), latest.market_caps.at(-1)];

                // Set number values
                this.marketcap = prefixNumber(current[1][1], this.$i18n.locale);
                this.price = current[0][1];

                if (history.status === 'rejected') {
                    throw history.reason;
                }

                const daily = history.value.prices
                    .concat(recent.value ? [current[0]] : [])
                    .filter(([t, v]) => Number.isFinite(t) && Number.isFinite(v))
                    .map(([t, v]) => ({ t, v }))
                    .sort((a, b) => a.t - b.t);

                // The widget only has to show the trend, so the days are averaged
                // three at a time and the curve is drawn smooth:
                this.points = Object.freeze(averagePoints(daily, WIDGET_POINTS));
            } catch (error) {
                console.error(error);
            }
        },

        axisFormat(value, locale, digits) {
            return value >= 10000
                ? '$' + compactNumber(value, locale, digits)
                : formatUsd(value, locale, false, digits);
        },
    },

    components: {
        UiStatsChart,
    },
};
</script>

<style lang="scss">
.widget-content:hover {
    text-decoration: none;
}
.widget-content--chart {
    display: block;
    // The curve sits close under the figures, the dates keep some room above
    // the bottom edge of the card:
    padding: 0 14px 12px 16px;
    cursor: pointer;

    .stats-chart {
        cursor: pointer;
        // Keeps the placeholder's top margin inside the 150px box instead of
        // collapsing through and pushing the whole block down:
        display: flow-root;
    }

    // The loading placeholder keeps the same gap under the figures as the
    // curve itself (plot top is 12px), so it doesn't touch the price skeletons:
    .stats-chart__skeleton {
        margin-top: 12px;
        height: calc(100% - 12px);
    }
}
.widget-type-market-cap, .widget-price-market-cap {
    text-align: right;
}
.widget-type {
    text-transform: uppercase;
    font-weight: 500;
}
</style>
